import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getAreaInfoByModel } from "../api/maps";
import { MapDataContext } from "../context/map-data";

interface GetColorFromValueProps {
  value: number;
  minValue: number;
  maxValue: number;
}

interface GetExtremesProps {
  bbox: [number, number, number, number ];
  update?: boolean;
}

interface Res {
  getColour: (value: number) => string;
  getExtremes: ({
    bbox,
    update,
  }: GetExtremesProps) => Promise<any>;
  lookupTable: string[];
  useMin: number | null;
  useMax: number | null;
  useMinLabel: string | null;
  useMaxLabel: string | null;
  valueFormat: string;
  showCustomScale: boolean;
  useReverse: boolean;
  useShowBands: boolean;
  minMaxAreInverted: boolean;
}

export default function useColourScale(): Res {
  const [lookupTable, setLookupTable] = useState<string[]>([])

  const { 
    currentLayer,
    scaleValues,
    zoom,
    setScaleValues,
    dataSourceModel,
  } = useContext(MapDataContext);

  const minMaxAreInverted = useMemo(() => {
    const {minValue, maxValue} = currentLayer || {}
    return !!(typeof minValue === 'number' && typeof maxValue === 'number' && minValue > maxValue)
  }, [currentLayer])

  const {useMin, useMax, useReverse, useShowBands, showCustomScale, useMinLabel, useMaxLabel, valueFormat } = useMemo(() => {
    let useMin = null
    let useMax = null
    let useReverse = false
    let useShowBands = false
    let useMinLabel = null
    let useMaxLabel = null
    let valueFormat = "0,0.00"

    const settingsZoom  = import.meta.env.VITE_MIN_CUSTOM_ZOOM ? Number(import.meta.env.VITE_MIN_CUSTOM_ZOOM) : 8
    const showCustomScale = !!(scaleValues && zoom >= settingsZoom )
    if (showCustomScale) {
      useMin = scaleValues.min
      useMax = scaleValues.max
      const r = scaleValues?.reverse_colors !== null ? scaleValues.reverse_colors : currentLayer?.reverse
      useReverse = r !== null && r !== undefined ? r : false
      const b = scaleValues?.show_bands !== null? scaleValues.show_bands : currentLayer?.show_bands
      useShowBands = b !== null && b !== undefined ? b : false
    }
    if (currentLayer) {
      if (useMax === null || useMax === undefined) {
        if (currentLayer.maxValue !== null && currentLayer.maxValue !== undefined) {
          useMax = Number(currentLayer.maxValue)
        }
      }
      if (useMin === null || useMin === undefined) {
        if (currentLayer.minValue !== null && currentLayer.minValue !== undefined) {
          useMin = Number(currentLayer.minValue)
        }
      }
      useMinLabel = currentLayer.minValueLabel || null
      useMaxLabel = currentLayer.maxValueLabel || null
      valueFormat = currentLayer.format || "0,0.00"
    }
    return {
      useMin: (useMin !== null && typeof useMin !== "string" && !isNaN(useMin)) ? useMin : null, 
      useReverse, 
      useShowBands, 
      useMax: (useMax !== null && typeof useMax !== "string" && !isNaN(useMax)) ? useMax : null, 
      showCustomScale,
      useMinLabel, 
      useMaxLabel, 
      valueFormat,
    }
  }, [scaleValues, currentLayer, zoom])

  const clamp = (v: number, min = 0, max = 1) =>
      Math.min(max, Math.max(min, v));


  useEffect(() => {
    if (currentLayer) {
      const { colours } = currentLayer;
    
      if (!colours || useMin === null || useMax === null) {
        setLookupTable([]);
        return;
      }
    
      const colorRamp = useReverse
        ? Object.values(colours)
        : Object.values(colours).reverse();
    
      const getColorFromValue = ({
        minValue,
        maxValue,
        value
      }: GetColorFromValueProps): string => {
        const scaled = (value - minValue) / (maxValue - minValue || 1);
        const t = clamp(scaled);

        if (useShowBands) {
          const bins = colorRamp.length;
          const binSize = 1 / bins;
          const bandIndex = Math.min(
            bins - 1,
            Math.floor(t / binSize)
          );
          return colorRamp[bandIndex];
        }
    
        // continuous (existing behaviour)
        const index = Math.floor(t * (colorRamp.length - 1));
        return colorRamp[index];
      };
    
      const table: string[] = Array(256)
        .fill(0)
        .map((_, i) => {
          const value = useMin + (i / 255) * (useMax - useMin);
          return getColorFromValue({
            minValue: useMin,
            maxValue: useMax,
            value
          });
        });
    
      setLookupTable(table);
    } else {
      setLookupTable([]);
    }
    
  }, [JSON.stringify(currentLayer), useMax, useMin, useReverse, useShowBands]);
  
  const getColour = useCallback((value: number): string => {
    if (!currentLayer || useMax === null || useMin === null) { 
      return ''
    }
    const t = clamp((value - useMin) / (useMax - useMin), 0, 1);
    const index = Math.floor(t * 255);
    return lookupTable[index] ?? '';
  }, [useMin, useMax, lookupTable, currentLayer]);
  
  const _setExtremes = useCallback((min: number | null, max: number | null) => {
    setScaleValues({
      min,
      max
    })
  }, [])

  const getExtremes = useCallback(async ({
    bbox,
    update = false,
  }: GetExtremesProps) => {
    if (!currentLayer || !dataSourceModel ) return;

    const { model, provider, geography, version } = dataSourceModel
    const {valueKey} = currentLayer

    const res = await getAreaInfoByModel({
      model,
      provider,
      geography,
      version,
      coords: bbox, 
      feature: valueKey || ''
    })
    const {error, response} = res
    if (error === 200 && response) {
      const {min_value, max_value} = response
      if (min_value !== null && max_value !== null) {
        const { minValue, maxValue } = currentLayer || {}
        const reversed = maxValue !==  null && minValue !== null && maxValue !== undefined && minValue !== undefined && maxValue < minValue
        const min = Number(min_value)
        const max = Number(max_value)
        // if (minValue !== undefined && maxValue !== undefined) {
        //   if (reversed) {
        //     _setExtremes(max, min)
        //   } else {
        //     _setExtremes(min, max)
        //   }
        // } else {
        //   _setExtremes(min, max)
        // }
        const val = {
          min: reversed? max : min,
          max: reversed? min : max,
        }
        if (update) {
          _setExtremes(val.min, val.max)
        }
        return val
      }
    }
    if (update) {
      _setExtremes(null, null)
    }
    return {
      min: null,
      max: null
    }
  }, [JSON.stringify(currentLayer), JSON.stringify(dataSourceModel)])


  return {
    minMaxAreInverted, 
    getColour, 
    useReverse, 
    useShowBands, 
    getExtremes, 
    lookupTable, 
    useMin, 
    useMax, 
    showCustomScale,
    useMinLabel,
    useMaxLabel,
    valueFormat,
  };
}